import { BaseModel } from "@/models/base.model"
import { type IBaseWithFiles } from "@/interfaces/api/base-with-files.interface"
import { ModelFactory } from "@/factories/model-factory"
import { FileBucket } from "@/models/file-bucket.model"
import { type IFileBucket } from "@/interfaces/api/file-bucket.interface"
import { v4 as uuidv4 } from "uuid"

export class BaseModelWithFiles extends BaseModel implements IBaseWithFiles {
  fileBucket: FileBucket

  constructor(base: IBaseWithFiles, managed = false) {
    super(base)

    this.fileBucket = ModelFactory.childModel(
      FileBucket,
      base.fileBucket ? base.fileBucket : new FileBucket({} as IFileBucket),
      managed
    )
  }

  public clone() {
    const clone = super.clone()
    clone.fileBucket = new FileBucket({ id: uuidv4() } as IFileBucket)
    return clone
  }
}

const OWN_BUCKET_KEYS = Symbol("ownBucketKeys")

// A mixin to add FileBuckets to a BaseModel (in addition to the default 'fileBucket' field)
export function WithExtraFileBuckets<K extends string>(...keys: readonly K[]) {
  return <TBase extends new (...a: any[]) => BaseModel>(Base: TBase) => {
    class Buckets extends Base {
      constructor(...args: any[]) {
        super(...args)
        const base = args[0] ?? {}
        const managed = args[1] ?? false
        const own: string[] = []

        for (const k of keys) {
          // If base class already created it (e.g., fileBucket), skip
          if ((this as any)[k] instanceof FileBucket) {
            continue
          }

          const existing: IFileBucket | undefined = base?.[k]
          ;(this as any)[k] = ModelFactory.childModel(
            FileBucket,
            existing ?? new FileBucket({} as IFileBucket),
            managed
          )
          own.push(k as string)
        }
        ;(this as any)[OWN_BUCKET_KEYS] = own
      }

      override clone(): this {
        const c = super.clone()
        const own: string[] = (this as any)[OWN_BUCKET_KEYS] ?? (keys as readonly [])
        for (const k of own) if ((c as any)[k]) (c as any)[k].id = uuidv4()
        ;(c as any)[OWN_BUCKET_KEYS] = own
        return c
      }
    }
    return Buckets as unknown as new (
      ...a: ConstructorParameters<TBase>
    ) => InstanceType<TBase> & { [P in K]: FileBucket }
  }
}
